import { useEffect, useRef, useState } from 'react';
import {
  X,
  Send,
  Download,
  Mail,
  MessageCircle,
  FileText,
  FileSpreadsheet,
  Loader as Loader2,
  CircleAlert as AlertCircle,
  TriangleAlert as AlertTriangle,
  CalendarDays,
  ArrowLeftRight,
} from 'lucide-react';
import { emitToast } from '@/components/ui/Toast';
import {
  buildExcelBlob,
  buildPdfBlob,
  downloadBlob,
  fetchWhatsAppAvailable,
  logReportDownload,
  reportFileName,
  saveClientEmail,
  sendStoreReport,
  todayIso,
  type ReportChannel,
  type ReportFormat,
} from '@/lib/consignmentStoreReport';
import { ConsignmentStoreReportDocument } from './ConsignmentStoreReportDocument';
import { useConsignmentStoreReport } from './useConsignmentStoreReport';

interface Props {
  companyId: string;
  clientId: string;
  clientName: string;
  initialAsOf?: string;
  onChangeStore?: () => void;
  onClose: () => void;
  onSent?: () => void;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function OptionButton({
  active, disabled, onClick, icon, label, badge,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all
        disabled:cursor-not-allowed disabled:opacity-50
        ${active
          ? 'border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-100'
          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`}
    >
      {icon}
      {label}
      {badge && (
        <span className="absolute -top-2 left-2 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold">
          {badge}
        </span>
      )}
    </button>
  );
}

export function SendConsignmentReportModal({
  companyId, clientId, clientName, initialAsOf, onChangeStore, onClose, onSent,
}: Props) {
  const [asOf, setAsOf] = useState(() => {
    const today = todayIso();
    return initialAsOf && initialAsOf <= today ? initialAsOf : today;
  });
  const [format, setFormat] = useState<ReportFormat>('pdf');
  const [channel, setChannel] = useState<ReportChannel>('email');
  const [email, setEmail] = useState('');
  const [whatsAppAvailable, setWhatsAppAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);

  const { report, loading, error } = useConsignmentStoreReport(companyId, clientId, asOf);
  const storedEmail = report?.client_email?.trim() || '';
  const phone = report?.client_phone?.trim() || '';

  useEffect(() => {
    fetchWhatsAppAvailable().then(setWhatsAppAvailable);
  }, []);

  useEffect(() => {
    if (report) setEmail(storedEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.client_id]);

  const reportMatchesStore = !!report && report.client_id === clientId;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit =
    reportMatchesStore && !loading && !busy &&
    (channel === 'download' ||
      (channel === 'email' && emailValid) ||
      (channel === 'whatsapp' && whatsAppAvailable && !!phone));

  const buildBlob = async () => {
    if (!report) throw new Error('הדוח לא נטען');
    if (format === 'excel') return buildExcelBlob(report);
    if (!pdfRef.current) throw new Error('הדוח לא נטען');
    return buildPdfBlob(pdfRef.current);
  };

  const handleSubmit = async () => {
    if (!report || !canSubmit) return;
    setBusy(true);
    try {
      const blob = await buildBlob();
      if (channel === 'download') {
        downloadBlob(blob, reportFileName(report, format));
        await logReportDownload(companyId, clientId, format, report.as_of);
        emitToast('הדוח הורד', 'success');
        onClose();
        return;
      }
      const recipient = channel === 'email' ? email.trim() : phone;
      if (channel === 'email' && !storedEmail) {
        try {
          await saveClientEmail(companyId, clientId, recipient);
        } catch (e) {
          emitToast(e instanceof Error ? e.message : 'שמירת המייל נכשלה', 'info');
        }
      }
      await sendStoreReport({ companyId, report, format, channel, recipient, blob });
      emitToast(channel === 'email' ? `הדוח נשלח אל ${recipient}` : 'הדוח נשלח בווטסאפ', 'success');
      onSent?.();
      onClose();
    } catch (e) {
      emitToast(e instanceof Error ? e.message : 'הפעולה נכשלה', 'error');
    } finally {
      setBusy(false);
    }
  };

  const submitLabel =
    channel === 'download' ? 'הורד' : channel === 'email' ? 'שלח במייל' : 'שלח בווטסאפ';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-2 sm:p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[94vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Send className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">שליחת יתרות לחנות</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-xs text-gray-500">{clientName}</p>
                {onChangeStore && (
                  <button
                    type="button"
                    onClick={onChangeStore}
                    disabled={busy}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                  >
                    <ArrowLeftRight className="w-3 h-3" />
                    החלף חנות
                  </button>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="סגור"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          <div className="lg:w-80 shrink-0 p-6 space-y-5 border-b lg:border-b-0 lg:border-l border-gray-100 overflow-y-auto">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-gray-500 flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5" />
                יתרה נכון לתאריך
              </label>
              <input
                type="date"
                value={asOf}
                max={todayIso()}
                onChange={(e) => e.target.value && setAsOf(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500">פורמט</p>
              <div className="flex gap-2">
                <OptionButton active={format === 'pdf'} onClick={() => setFormat('pdf')} icon={<FileText className="w-4 h-4" />} label="PDF" />
                <OptionButton active={format === 'excel'} onClick={() => setFormat('excel')} icon={<FileSpreadsheet className="w-4 h-4" />} label="אקסל" />
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500">פעולה</p>
              <div className="flex gap-2">
                <OptionButton active={channel === 'download'} onClick={() => setChannel('download')} icon={<Download className="w-4 h-4" />} label="הורדה" />
                <OptionButton active={channel === 'email'} onClick={() => setChannel('email')} icon={<Mail className="w-4 h-4" />} label="מייל" />
                <OptionButton
                  active={channel === 'whatsapp'}
                  disabled={!whatsAppAvailable}
                  onClick={() => setChannel('whatsapp')}
                  icon={<MessageCircle className="w-4 h-4" />}
                  label="ווטסאפ"
                  badge={whatsAppAvailable ? undefined : 'בקרוב'}
                />
              </div>
            </div>

            {channel === 'email' && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-500">כתובת מייל</label>
                {!loading && report && !storedEmail && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-amber-800 leading-relaxed">
                      לחנות אין כתובת מייל. הכתובת שתזין תישמר בכרטיס החנות.
                    </p>
                  </div>
                )}
                <input
                  type="email"
                  dir="ltr"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-3 py-2.5 border rounded-xl text-sm outline-none focus:ring-2 transition-all
                    ${email && !emailValid ? 'border-red-300 focus:ring-red-100' : 'border-gray-200 focus:ring-blue-200 focus:border-blue-400'}`}
                />
                {email && !emailValid && <p className="text-xs text-red-600">כתובת מייל לא תקינה</p>}
              </div>
            )}

            {channel === 'whatsapp' && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500">טלפון החנות</p>
                <p className="px-3 py-2.5 bg-gray-50 rounded-xl text-sm" dir="ltr">{phone || '—'}</p>
                {!phone && <p className="text-xs text-red-600">לחנות לא מוגדר מספר טלפון</p>}
              </div>
            )}
          </div>

          <div className="flex-1 min-h-[320px] bg-gray-100 overflow-auto p-4">
            {loading && (
              <div className="h-full flex items-center justify-center gap-2 text-gray-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin" />
                מכין תצוגה מקדימה…
              </div>
            )}
            {!loading && error && (
              <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}
            {!loading && report && (
              <div className="shadow-sm rounded-lg overflow-hidden w-fit min-w-full">
                <ConsignmentStoreReportDocument report={report} />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/60">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
          >
            ביטול
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700
                       rounded-xl shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : channel === 'download' ? <Download className="w-4 h-4" /> : <Send className="w-4 h-4" />}
            {submitLabel}
          </button>
        </div>
      </div>

      {report && (
        <div className="fixed top-0 -left-[10000px] w-[900px] pointer-events-none" aria-hidden="true">
          <ConsignmentStoreReportDocument ref={pdfRef} report={report} />
        </div>
      )}
    </div>
  );
}
